<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { BaseModal, BaseButton } from '@/components'
import type { LeadLinkDto } from '../api/types'

const props = defineProps<{
    modelValue: boolean
    isSubmitting?: boolean
}>()

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'add-link': [payload: LeadLinkDto]
}>()

const targetType = ref<LeadLinkDto['targetType']>('property')
const targetId = ref('')
const label = ref('')
const url = ref('')

const targetTypeOptions: { value: LeadLinkDto['targetType']; label: string }[] = [
    { value: 'property', label: 'Internal Property' },
    { value: 'contact', label: 'Internal Contact' },
    { value: 'tenant', label: 'Internal Tenant' },
    { value: 'project', label: 'Internal Project' },
    { value: 'weather_event', label: 'Severe Weather Event' },
    { value: 'campaign_url', label: 'Campaign URL' },
    { value: 'custom_url', label: 'External Website URL' },
]

const isUrlType = computed(() => targetType.value === 'custom_url' || targetType.value === 'campaign_url')

watch(() => props.modelValue, (isOpen) => {
    if (isOpen) {
        targetType.value = 'property'
        targetId.value = ''
        label.value = ''
        url.value = ''
    }
})

function handleAdd() {
    emit('add-link', {
        targetType: targetType.value,
        targetId: targetId.value.trim() || undefined,
        label: label.value.trim() || undefined,
        url: url.value.trim() || undefined,
    })
}
</script>

<template>
    <BaseModal
        :model-value="modelValue"
        title="Attach Reference Link"
        size="md"
        @update:model-value="(v) => emit('update:modelValue', v)"
    >
        <div class="add-link-modal">
            <div class="form-group">
                <label class="form-label" for="link-target-type">Target Record Type</label>
                <select
                    id="link-target-type"
                    v-model="targetType"
                    class="form-input form-select"
                >
                    <option v-for="opt in targetTypeOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                    </option>
                </select>
            </div>

            <div class="form-group">
                <label class="form-label" for="link-label">Display Label (Optional)</label>
                <input
                    id="link-label"
                    v-model="label"
                    type="text"
                    class="form-input"
                    placeholder="e.g. Dallas HQ, Building B, Estimating Sheet"
                />
            </div>

            <div v-if="!isUrlType" class="form-group">
                <label class="form-label" for="link-target-id">Record UUID / ID</label>
                <input
                    id="link-target-id"
                    v-model="targetId"
                    type="text"
                    class="form-input"
                    placeholder="e.g. 66f17d21-a0fb-4dfd-bc62-a29b91536cec"
                />
            </div>

            <div v-else class="form-group">
                <label class="form-label" for="link-url">URL</label>
                <input
                    id="link-url"
                    v-model="url"
                    type="url"
                    class="form-input"
                    placeholder="https://..."
                />
            </div>
        </div>

        <template #footer>
            <div class="modal-actions">
                <button
                    type="button"
                    class="action-link"
                    :disabled="isSubmitting"
                    @click="emit('update:modelValue', false)"
                >
                    Cancel
                </button>
                <BaseButton
                    variant="primary"
                    :loading="isSubmitting"
                    @click="handleAdd"
                >
                    Attach Link
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<style scoped>
.add-link-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.form-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
}

.form-input {
    padding: 0.5rem 0.625rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.form-select {
    cursor: pointer;
}

.form-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.modal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    width: 100%;
}

.action-link {
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    cursor: pointer;
    padding: 0.375rem 0.75rem;
    transition: color 0.15s;
}

.action-link:hover {
    color: var(--text-primary);
}
</style>
